import { Controller, type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Field, FieldError, FieldLabel, Input } from '@clouway/ui'

// The name/description fields, mirroring the legacy RoleForm's shape and
// validation precedent (zod + react-hook-form). CustomRole has no separate
// display-name field server-side, so `id` doubles as both the role's
// identifier and its human-facing label — immutable once created, hence
// disabled rather than omitted in edit mode (the value still round-trips
// through the form as the existing id).
//
// Unlike the legacy form this isn't a self-contained <form>: the tier matrix
// lives outside react-hook-form (its state isn't text-input shaped), so the
// owning editor (RoleEditor) holds the single useForm/handleSubmit call and
// passes its `control` down here — the common react-hook-form pattern for
// splitting field groups across components without lifting values manually.
export interface RoleFormValues {
  id: string
  description: string
}

export function RoleForm({
  control,
  mode,
  readOnly = false,
}: {
  control: Control<RoleFormValues>
  mode: 'create' | 'edit'
  readOnly?: boolean
}) {
  const { t } = useTranslation('feature')

  return (
    <div className="flex flex-col gap-4">
      <Controller
        control={control}
        name="id"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="role-id">{t('role.form.id')}</FieldLabel>
            <Input id="role-id" {...field} disabled={readOnly || mode === 'edit'} />
            {fieldState.error ? (
              <FieldError>{fieldState.error.message}</FieldError>
            ) : (
              <p className="text-muted-foreground text-xs">{t('role.form.idHelp')}</p>
            )}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="description"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="role-description">{t('role.form.description')}</FieldLabel>
            <Input id="role-description" {...field} disabled={readOnly} />
          </Field>
        )}
      />
    </div>
  )
}
